import { useMutation, useQuery, useQueryClient, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import {
  createCloudMaterial,
  fetchAsinMaterials,
  fetchCloudMaterials,
  fetchTemporaryMaterials,
  fetchMaterialWorkflowStages,
  updateCloudMaterial,
  updateTemporaryMaterial,
  uploadTemporaryMaterial,
  uploadCloudMaterial,
  uploadDirectMaterial,
  requestDeleteTemporary,
  requestSaveTemporary,
  requestDeleteCloudMaterial,
  moveCloudMaterialToPersonal,
  fetchDeletedTemporaryMaterials,
  fetchDeletedCloudMaterials,
  restoreTemporaryMaterial,
  restoreCloudMaterial,
  permanentlyDeleteTemporaryMaterial,
  permanentlyDeleteCloudMaterial,
  bulkDeleteTemporaryMaterials,
  bulkDeleteCloudMaterials,
  ensureMaterialRaster,
  type MaterialFolderType,
  type MaterialFilters,
  type TrashFilters,
} from './api';
import type { CreateCloudMaterialPayload, UpdateCloudMaterialPayload } from './materialsSlice';
import { useToast } from '../../hooks/useToast';
import { uploadSuccessMessage, useMaterialUploadFeedback } from './useUploadFeedback';

export const TEMP_QUERY_KEY = ['temporary-materials'];
export const CLOUD_QUERY_KEY = ['cloud-materials'];
const TEMP_TRASH_QUERY_KEY = ['materials', 'temporary', 'trash'];
const CLOUD_TRASH_QUERY_KEY = ['materials', 'cloud', 'trash'];

export const PAGE_LIMIT = 60;

/**
 * 個人フォルダの素材一覧取得（フィルタ対応）
 * @param filters フィルタパラメータ（オプション）
 */
export function useTemporaryMaterials(filters?: MaterialFilters, opts?: { enabled?: boolean }) {
  return useInfiniteQuery({
    // フィルタをqueryKeyに含めることでフィルタ変更時に新しいクエリが実行される
    queryKey: [...TEMP_QUERY_KEY, filters],
    queryFn: ({ pageParam }) =>
      fetchTemporaryMaterials({ limit: PAGE_LIMIT, cursor: pageParam ?? null, filters }),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: null as string | null,
    placeholderData: keepPreviousData, // invalidateQueries時にキャッシュ再構築中も前回データを維持
    enabled: opts?.enabled ?? true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // 1s, 2s, 4s... 最大10s
  });
}

/**
 * 共有フォルダの素材一覧取得（フィルタ対応）
 * @param filters フィルタパラメータ（オプション）
 */
export function useCloudMaterials(filters?: MaterialFilters, opts?: { enabled?: boolean }) {
  return useInfiniteQuery({
    // フィルタをqueryKeyに含めることでフィルタ変更時に新しいクエリが実行される
    queryKey: [...CLOUD_QUERY_KEY, filters],
    queryFn: ({ pageParam }) =>
      fetchCloudMaterials({ limit: PAGE_LIMIT, cursor: pageParam ?? null, filters }),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: null as string | null,
    placeholderData: keepPreviousData, // invalidateQueries時にキャッシュ再構築中も前回データを維持
    staleTime: 5 * 60 * 1000, // 5分間はキャッシュを新鮮とみなす
    refetchOnWindowFocus: false, // ウィンドウフォーカス時の自動再フェッチを無効化
    enabled: opts?.enabled ?? true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // 1s, 2s, 4s... 最大10s
  });
}

// ============================================================
// ページ番号ジャンプ用 (素材ブラウズページ専用)
//   - 右パネル/ピッカーは上記の useInfiniteQuery (カーソル) のまま
//   - こちらは offset ページングで「現在ページのみ」を取得する
// ============================================================

/**
 * 個人フォルダ: 指定ページ (1始まり) のみを offset で取得。
 */
export function useTemporaryMaterialsPaged(filters: MaterialFilters | undefined, page: number, enabled = true) {
  return useQuery({
    queryKey: [...TEMP_QUERY_KEY, 'paged', filters, page],
    queryFn: () =>
      fetchTemporaryMaterials({ limit: PAGE_LIMIT, offset: (page - 1) * PAGE_LIMIT, filters }),
    placeholderData: keepPreviousData, // ページ移動中も前ページを表示
    enabled,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * 共有フォルダ: 指定ページ (1始まり) のみを offset で取得。
 */
export function useCloudMaterialsPaged(filters: MaterialFilters | undefined, page: number, enabled = true) {
  return useQuery({
    queryKey: [...CLOUD_QUERY_KEY, 'paged', filters, page],
    queryFn: () =>
      fetchCloudMaterials({ limit: PAGE_LIMIT, offset: (page - 1) * PAGE_LIMIT, filters }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * 両タブの「件数 + ファセット」だけを軽量取得 (offset=0, limit=1)。
 * ページ番号ジャンプ時、非アクティブタブのタブ件数表示と、両タブをマージした
 * フィルタ選択肢 (facets) を、現在ページに依存せず安定して供給するための専用クエリ。
 * items は使わない (total_count / facets のみ参照)。
 */
export function useMaterialsPagedMeta(filters: MaterialFilters | undefined, enabled = true) {
  const cloud = useQuery({
    queryKey: [...CLOUD_QUERY_KEY, 'meta', filters],
    queryFn: () => fetchCloudMaterials({ limit: 1, offset: 0, filters }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
  const temporary = useQuery({
    queryKey: [...TEMP_QUERY_KEY, 'meta', filters],
    queryFn: () => fetchTemporaryMaterials({ limit: 1, offset: 0, filters }),
    placeholderData: keepPreviousData,
    enabled,
  });
  return { cloud, temporary };
}

// MaterialFiltersとMaterialFacetsをre-exportして他のコンポーネントから使えるようにする
export type { MaterialFilters, MaterialFacets, MaterialWorkflowStage } from './api';

/**
 * 現在組織で有効な素材制作進捗 (workflow stage) の一覧を取得。
 * 空配列が返った場合、組織でこの機能が未設定 (= 非表示) を意味する。
 */
export function useMaterialWorkflowStages() {
  return useQuery({
    queryKey: ['material-workflow-stages'],
    queryFn: fetchMaterialWorkflowStages,
    staleTime: 10 * 60 * 1000,
  });
}

export function useFetchAsinMaterials() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fetchAsinMaterials,
    onSuccess: async () => {
      toast.show('素材を読み込んでいます...');
      await queryClient.refetchQueries({ queryKey: TEMP_QUERY_KEY });
      toast.success('商品画像を取得しました');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || '商品画像の取得に失敗しました';
      toast.error(`取得エラー: ${message}`);
      console.error('Fetch ASIN materials error:', error);
    },
  });
}

export function useUploadTemporaryMaterial() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { showUploadError } = useMaterialUploadFeedback();
  return useMutation({
    mutationFn: uploadTemporaryMaterial,
    onSuccess: async (result) => {
      toast.show('素材を読み込んでいます...');
      await queryClient.refetchQueries({ queryKey: TEMP_QUERY_KEY });
      toast.success(uploadSuccessMessage(result, '個人フォルダ'));
    },
    onError: (error: any) => {
      // SVG (レイヤー素材) の入稿エラーは error_code で文言を出し分ける (useUploadFeedback)
      showUploadError(error);
      console.error('Upload error:', error);
    },
  });
}

export function useUploadCloudMaterial() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { showUploadError } = useMaterialUploadFeedback();
  return useMutation({
    mutationFn: uploadCloudMaterial,
    onSuccess: async (result) => {
      toast.show('素材を読み込んでいます...');
      await queryClient.refetchQueries({ queryKey: CLOUD_QUERY_KEY });
      toast.success(uploadSuccessMessage(result, '共有フォルダ'));
    },
    onError: (error: any) => {
      // SVG (レイヤー素材) の入稿エラーは error_code で文言を出し分ける (useUploadFeedback)
      showUploadError(error);
      console.error('Upload error:', error);
    },
  });
}

/**
 * 動画/音声の直接アップロード (ブラウザ → GCS 直送。2GB まで)。
 * docs/features/large-file-direct-upload-plan.md v5
 */
export function useUploadDirectMaterial() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: uploadDirectMaterial,
    onSuccess: async (_result, variables) => {
      toast.show('素材を読み込んでいます...');
      const queryKey = variables.destination === 'temporary' ? TEMP_QUERY_KEY : CLOUD_QUERY_KEY;
      await queryClient.refetchQueries({ queryKey });
      const folderLabel = variables.destination === 'temporary' ? '個人フォルダ' : '共有フォルダ';
      toast.success(`${folderLabel}に素材をアップロードしました`);
    },
    onError: (error: any) => {
      if (error?.errorCode === 'ABORTED') {
        // ユーザー操作によるキャンセルはエラー表示にしない
        toast.show('アップロードをキャンセルしました');
        return;
      }
      const message = error?.response?.data?.error || error?.message || 'アップロードに失敗しました';
      toast.error(`アップロードエラー: ${message}`);
      console.error('Direct upload error:', error);
    },
  });
}

export function useUpdateTemporaryMaterial(materialId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { asin?: string | string[]; protected?: boolean }) => updateTemporaryMaterial(materialId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
    },
  });
}

export function useDeleteTemporaryMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestDeleteTemporary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEMP_TRASH_QUERY_KEY });
    },
  });
}

export function useSaveTemporaryMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestSaveTemporary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
    },
  });
}

export function useCreateCloudMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCloudMaterialPayload) => createCloudMaterial(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
    },
  });
}

export function useUpdateCloudMaterial(materialId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCloudMaterialPayload) => updateCloudMaterial(materialId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
    },
  });
}

export function useDeleteCloudMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestDeleteCloudMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CLOUD_TRASH_QUERY_KEY });
    },
  });
}

export function useMoveCloudMaterialToPersonal() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: moveCloudMaterialToPersonal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
      toast.success('素材を個人フォルダに移動しました');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || '移動に失敗しました';
      toast.error(`移動エラー: ${message}`);
      console.error('Move to personal error:', error);
    },
  });
}

// ============================================================
// ゴミ箱機能 (Trash) Hooks
// ============================================================

// TrashFiltersをre-exportして他のコンポーネントから使えるようにする
export type { TrashFilters } from './api';

export function useDeletedTemporaryMaterials(params?: { limit?: number; cursor?: string | null; filters?: TrashFilters }) {
  return useQuery({
    queryKey: [...TEMP_TRASH_QUERY_KEY, params],
    queryFn: () => fetchDeletedTemporaryMaterials(params),
  });
}

export function useDeletedCloudMaterials(params?: { limit?: number; cursor?: string | null; filters?: TrashFilters }) {
  return useQuery({
    queryKey: [...CLOUD_TRASH_QUERY_KEY, params],
    queryFn: () => fetchDeletedCloudMaterials(params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useRestoreTemporaryMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: restoreTemporaryMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TEMP_TRASH_QUERY_KEY });
    },
  });
}

export function useRestoreCloudMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: restoreCloudMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CLOUD_TRASH_QUERY_KEY });
    },
  });
}

export function usePermanentlyDeleteTemporaryMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: permanentlyDeleteTemporaryMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_TRASH_QUERY_KEY });
    },
  });
}

export function usePermanentlyDeleteCloudMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: permanentlyDeleteCloudMaterial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_TRASH_QUERY_KEY });
    },
  });
}

export function useBulkDeleteTemporaryMaterials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteTemporaryMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMP_TRASH_QUERY_KEY });
    },
  });
}

export function useBulkDeleteCloudMaterials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteCloudMaterials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLOUD_TRASH_QUERY_KEY });
    },
  });
}

/**
 * 非ラスタ原本 (SVG 等) のラスタ版を lazy 生成する mutation。
 * 成功時は該当フォルダの一覧を invalidate し、カードの raster_status / サムネイルを最新にする。
 * ラスタ版が要る操作の直前に呼ぶ用途は useEnsureRasterAsset (ensureRasterAsset.ts) を使う。
 */
export function useEnsureMaterialRaster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folderType, materialId, force }: { folderType: MaterialFolderType; materialId: string; force?: boolean }) =>
      ensureMaterialRaster(folderType, materialId, { force }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: variables.folderType === 'temporary' ? TEMP_QUERY_KEY : CLOUD_QUERY_KEY });
    },
  });
}
